import * as stylex from "@stylexjs/stylex";
import {
  colors,
  fontSizes,
  spacing,
} from "@softmax/design-system/tokens.stylex";
import { Fragment, type ComponentPropsWithRef, type ReactNode } from "react";

export type PlayOnePagerLink = {
  readonly label: string;
  readonly href: string;
  readonly external: boolean;
};

export type OnePagerLinkRenderer = (
  props: Readonly<ComponentPropsWithRef<"a"> & { href: string }>,
) => ReactNode;

export function OnePagerLinks({
  name,
  site,
  links,
  renderLink,
}: {
  readonly name: string;
  readonly site: { readonly label: string; readonly href: string } | null;
  readonly links: readonly PlayOnePagerLink[];
  readonly renderLink: OnePagerLinkRenderer;
}) {
  return (
    <nav {...stylex.props(styles.links)} aria-label={name + " links"}>
      {site ? (
        <a
          {...stylex.props(styles.visit)}
          href={site.href}
          target="_blank"
          rel="noreferrer"
        >
          Visit {site.label} ↗
        </a>
      ) : null}
      {links.map((link) =>
        link.external ? (
          <a
            key={link.href}
            {...stylex.props(styles.link)}
            href={link.href}
            target="_blank"
            rel="noreferrer"
          >
            {link.label} ↗
          </a>
        ) : (
          <Fragment key={link.href}>
            {renderLink({
              ...stylex.props(styles.link),
              href: link.href,
              children: link.label,
            })}
          </Fragment>
        ),
      )}
    </nav>
  );
}

const styles = stylex.create({
  links: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    rowGap: spacing.space8,
    columnGap: 18,
  },
  visit: {
    display: "inline-flex",
    alignItems: "center",
    height: 38,
    paddingBlock: 0,
    paddingInline: 18,
    borderRadius: 999,
    backgroundColor: {
      default: colors.playPaper,
      ":hover":
        "color-mix(in srgb, var(--accent) 18%, var(--softmax-color-play-paper))",
    },
    color: colors.playInk,
    fontWeight: 600,
    textDecoration: "none",
  },
  link: {
    color: colors.playPaper,
    fontSize: fontSizes.reading,
    textDecoration: "none",
    textUnderlineOffset: 4,
  },
});
